/**
 * Recipe lifecycle commands: create, restore, delete, and the scalar setters.
 *
 * Every setter follows one shape — apply writes the new value through
 * `withRecipe` (which stamps `updatedAt`), and invert re-issues the same
 * command with the pre-state's value and the pre-state's `updatedAt` as its
 * envelope timestamp. That is the whole undo mechanism for scalar fields.
 */

import { PRIMING_SUGAR_LABELS } from '@beerwright/core';
import { PACKAGING_METHOD_LABELS } from '../../document/common';
import { blankRecipeDoc, type RecipeDoc } from '../../document/recipe';
import { rescaleEfficiencyDoc, scaleRecipeDoc } from '../../document/scale';
import type { WorkspaceState } from '../../document/workspace';
import { RECIPE_SCHEMA_VERSION } from '../../version';
import { defineCommand, inverseCommand } from '../definition';
import { commandError } from '../envelope';
import { num, percent, quoted, transition, volume } from '../format';
import { dropRecipe, putRecipe, withRecipe } from '../helpers';
import * as p from '../payloads';

/** Name a blank recipe takes when the caller supplies none. */
export const UNTITLED_RECIPE_NAME = 'Untitled recipe';

/** Look up a recipe for a summary, tolerating its absence (summaries never throw). */
function recipeName(state: WorkspaceState, recipeId: string): string {
  return state.recipes[recipeId]?.name ?? recipeId;
}

/**
 * Create a recipe — blank, or cloned from a supplied document.
 *
 * A blank recipe adopts the workspace's defaults (batch size, boil time,
 * equipment profile) so "new recipe" lands on the user's usual system rather
 * than on this package's constants.
 */
export const recipeCreate = defineCommand({
  type: 'recipe.create',
  payload: p.recipeCreatePayloadSchema,
  apply: (state, command) => {
    const { recipeId, name, from } = command.payload;
    if (state.recipes[recipeId] !== undefined) {
      return {
        ok: false,
        error: commandError('conflict', `A recipe with id "${recipeId}" already exists.`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    const doc: RecipeDoc =
      from === undefined
        ? {
            ...blankRecipeDoc({
              id: recipeId,
              name: name ?? UNTITLED_RECIPE_NAME,
              timestamp: command.timestamp,
            }),
            batchVolumeL: state.settings.defaultBatchVolumeL,
            boilTimeMinutes: state.settings.defaultBoilTimeMinutes,
            equipmentProfileId: state.settings.defaultEquipmentProfileId,
          }
        : {
            ...from,
            schemaVersion: RECIPE_SCHEMA_VERSION,
            id: recipeId,
            name: name ?? from.name,
            createdAt: command.timestamp,
            updatedAt: command.timestamp,
          };
    return { ok: true, state: putRecipe(state, doc) };
  },
  invert: (_state, command) =>
    inverseCommand(
      command,
      'recipe.delete',
      { recipeId: command.payload.recipeId },
      command.timestamp,
    ),
  summarize: (_state, command) => {
    const label = command.payload.name ?? command.payload.from?.name ?? UNTITLED_RECIPE_NAME;
    return command.payload.from === undefined
      ? `Create recipe ${quoted(label)}`
      : `Create recipe ${quoted(label)} from an existing document`;
  },
});

/**
 * Insert a recipe verbatim, timestamps included — an **upsert**.
 *
 * Three jobs: undo of `recipe.delete`, undo of any command whose precise
 * inverse would be lossy (`recipe.scale`, because `k` then `1/k` is not exact
 * in binary floating point), and the delta a full-state sync push carries when
 * a client is too far behind for incremental catch-up (PLAN §8.3).
 */
export const recipeRestore = defineCommand({
  type: 'recipe.restore',
  payload: p.recipeRestorePayloadSchema,
  apply: (state, command) => ({ ok: true, state: putRecipe(state, command.payload.recipe) }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipe.id];
    return prior === undefined
      ? inverseCommand(
          command,
          'recipe.delete',
          { recipeId: command.payload.recipe.id },
          command.timestamp,
        )
      : inverseCommand(command, 'recipe.restore', { recipe: prior }, prior.updatedAt);
  },
  summarize: (_state, command) => `Restore recipe ${quoted(command.payload.recipe.name)}`,
});

/** Delete a recipe. Invertible: the inverse carries the whole prior document. */
export const recipeDelete = defineCommand({
  type: 'recipe.delete',
  payload: p.recipeDeletePayloadSchema,
  apply: (state, command) => {
    const recipe = state.recipes[command.payload.recipeId];
    if (recipe === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No recipe with id "${command.payload.recipeId}".`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    return { ok: true, state: dropRecipe(state, command.payload.recipeId) };
  },
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(command, 'recipe.restore', { recipe: prior }, prior.updatedAt);
  },
  summarize: (state, command) =>
    `Delete recipe ${quoted(recipeName(state, command.payload.recipeId))}`,
});

/** Rename a recipe. */
export const recipeRename = defineCommand({
  type: 'recipe.rename',
  payload: p.recipeRenamePayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      name: command.payload.name,
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.rename',
          { recipeId: prior.id, name: prior.name },
          prior.updatedAt,
        );
  },
  summarize: (state, command) =>
    `Rename recipe ${transition(
      quoted(recipeName(state, command.payload.recipeId)),
      quoted(command.payload.name),
    )}`,
});

/** Set or clear the BJCP style. */
export const recipeSetStyle = defineCommand({
  type: 'recipe.setStyle',
  payload: p.recipeSetStylePayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      styleId: command.payload.styleId,
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.setStyle',
          { recipeId: prior.id, styleId: prior.styleId },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) =>
    command.payload.styleId === null
      ? 'Clear the recipe style'
      : `Set style to ${command.payload.styleId}`,
});

/** Set the target batch volume, and optionally what it measures. */
export const recipeSetBatchSize = defineCommand({
  type: 'recipe.setBatchSize',
  payload: p.recipeSetBatchSizePayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      batchVolumeL: command.payload.batchVolumeL,
      batchVolumeBasis: command.payload.batchVolumeBasis ?? recipe.batchVolumeBasis,
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.setBatchSize',
          {
            recipeId: prior.id,
            batchVolumeL: prior.batchVolumeL,
            batchVolumeBasis: prior.batchVolumeBasis,
          },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    const basis = command.payload.batchVolumeBasis ?? prior?.batchVolumeBasis ?? 'fermenter';
    const to = `${volume(command.payload.batchVolumeL)} (${basis})`;
    return prior === undefined
      ? `Set batch size to ${to}`
      : `Set batch size ${transition(volume(prior.batchVolumeL), to)}`;
  },
});

/** Set the boil length. */
export const recipeSetBoilTime = defineCommand({
  type: 'recipe.setBoilTime',
  payload: p.recipeSetBoilTimePayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      boilTimeMinutes: command.payload.boilTimeMinutes,
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.setBoilTime',
          { recipeId: prior.id, boilTimeMinutes: prior.boilTimeMinutes },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) => `Set boil time to ${num(command.payload.boilTimeMinutes)} min`,
});

/** Point the recipe at an equipment profile. */
export const recipeSetEquipment = defineCommand({
  type: 'recipe.setEquipment',
  payload: p.recipeSetEquipmentPayloadSchema,
  apply: (state, command) => {
    const { equipmentProfileId } = command.payload;
    if (equipmentProfileId !== null && state.equipmentProfiles[equipmentProfileId] === undefined) {
      return {
        ok: false,
        error: commandError('not-found', `No equipment profile with id "${equipmentProfileId}".`, {
          commandId: command.commandId,
          commandType: command.type,
        }),
      };
    }
    return withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      equipmentProfileId,
    }));
  },
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.setEquipment',
          { recipeId: prior.id, equipmentProfileId: prior.equipmentProfileId },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const { equipmentProfileId } = command.payload;
    if (equipmentProfileId === null) return 'Use the workspace default equipment profile';
    const name = state.equipmentProfiles[equipmentProfileId]?.name ?? equipmentProfileId;
    return `Set equipment profile to ${quoted(name)}`;
  },
});

/**
 * Override this recipe's mash efficiency, or clear the override with `null`.
 *
 * Deliberately *not* folded into `recipe.setEquipment`: the two are independent
 * choices — a brewer who moves a stout onto their BIAB rig keeps the stout's
 * low-efficiency override — and undo should be able to take back the number
 * without taking back the rig.
 */
export const recipeSetEfficiency = defineCommand({
  type: 'recipe.setEfficiency',
  payload: p.recipeSetEfficiencyPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      mashEfficiencyPercentOverride: command.payload.mashEfficiencyPercentOverride,
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.setEfficiency',
          {
            recipeId: prior.id,
            mashEfficiencyPercentOverride: prior.mashEfficiencyPercentOverride,
          },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) => {
    const { mashEfficiencyPercentOverride } = command.payload;
    return mashEfficiencyPercentOverride === null
      ? "Use the equipment profile's mash efficiency"
      : `Set mash efficiency to ${percent(mashEfficiencyPercentOverride)} for this recipe`;
  },
});

/**
 * State the OG this recipe is aiming at.
 *
 * Its own command rather than a field on a general recipe update, like every
 * other piece of recipe metadata here: one narrow command per field is what
 * makes the undo entry say what was actually done, and what keeps a defaulted
 * field from being written as an explicit null by an edit to something else.
 *
 * Clearing it to `null` is a real edit and gets its own sentence. It does not
 * touch the grist: an intention withdrawn should not rescale a recipe.
 */
export const recipeSetTargetOg = defineCommand({
  type: 'recipe.setTargetOg',
  payload: p.recipeSetTargetOgPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      targetOg: command.payload.targetOg,
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.setTargetOg',
          { recipeId: prior.id, targetOg: prior.targetOg },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) => {
    const { targetOg } = command.payload;
    return targetOg === null
      ? "Aim at the style's own gravity"
      : `Set target OG to ${targetOg.toFixed(3)}`;
  },
});

/**
 * State the colour this recipe is aiming at.
 *
 * {@link recipeSetTargetOg}'s twin on the other number the grist decides, and
 * the same shape down to the nullability: a value is the brewer's intention, and
 * `null` hands the question back to the style.
 *
 * Like the gravity target it carries the whole value rather than a delta, so the
 * inverse is the previous colour and undo is exact. The share rewrite that
 * usually accompanies it is a separate set of commands batched alongside this
 * one, so a brewer takes back the intention and the malts it moved together.
 */
export const recipeSetTargetSrm = defineCommand({
  type: 'recipe.setTargetSrm',
  payload: p.recipeSetTargetSrmPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      targetSrm: command.payload.targetSrm,
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.setTargetSrm',
          { recipeId: prior.id, targetSrm: prior.targetSrm },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) => {
    const { targetSrm } = command.payload;
    return targetSrm === null
      ? "Follow the style's color again"
      : `Set target color to ${num(targetSrm, 1)} SRM`;
  },
});

/**
 * Put a recipe away, or bring it back (PLAN-ARCHIVE B).
 *
 * The date is stamped here rather than sent, from the same `command.timestamp`
 * {@link withRecipe} uses for `updatedAt`, so a replayed log lands on the date
 * it landed on the first time.
 *
 * Archiving is a statement about the *list*. The recipe is still held, still
 * synced, still counted against the plan's recipe cap, and anything already
 * pointing at it — a share link, a gallery listing, a batch — is untouched.
 */
export const recipeSetArchived = defineCommand({
  type: 'recipe.setArchived',
  payload: p.recipeSetArchivedPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      archivedAt: command.payload.archived ? command.timestamp : null,
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.setArchived',
          { recipeId: prior.id, archived: prior.archivedAt !== null },
          prior.updatedAt,
        );
  },
  summarize: (state, command) => {
    const name = state.recipes[command.payload.recipeId]?.name ?? 'recipe';
    return command.payload.archived ? `Archive ${name}` : `Restore ${name}`;
  },
});

/** Set the target carbonation. */
export const recipeSetCarbonation = defineCommand({
  type: 'recipe.setCarbonation',
  payload: p.recipeSetCarbonationPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      carbonationVolumes: command.payload.carbonationVolumes,
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.setCarbonation',
          { recipeId: prior.id, carbonationVolumes: prior.carbonationVolumes },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) =>
    `Set carbonation to ${num(command.payload.carbonationVolumes, 2)} volumes CO₂`,
});

/** Set how the beer is packaged and what primes it. */
export const recipeSetPackaging = defineCommand({
  type: 'recipe.setPackaging',
  payload: p.recipeSetPackagingPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      // Spread the payload rather than assigning both: an omitted key means
      // "leave it", and reading `payload.primingSugar` straight into the doc
      // would write `undefined` over the brewer's choice.
      ...(command.payload.packagingMethod === undefined
        ? {}
        : { packagingMethod: command.payload.packagingMethod }),
      ...(command.payload.primingSugar === undefined
        ? {}
        : { primingSugar: command.payload.primingSugar }),
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    if (prior === undefined) return null;
    // Invert only what the command actually set, so undoing a method change
    // does not also restate a sugar the command never touched.
    return inverseCommand(
      command,
      'recipe.setPackaging',
      {
        recipeId: prior.id,
        ...(command.payload.packagingMethod === undefined
          ? {}
          : { packagingMethod: prior.packagingMethod }),
        ...(command.payload.primingSugar === undefined ? {} : { primingSugar: prior.primingSugar }),
      },
      prior.updatedAt,
    );
  },
  summarize: (_state, command) => {
    const parts: string[] = [];
    if (command.payload.packagingMethod !== undefined) {
      parts.push(PACKAGING_METHOD_LABELS[command.payload.packagingMethod]);
    }
    if (command.payload.primingSugar !== undefined) {
      parts.push(PRIMING_SUGAR_LABELS[command.payload.primingSugar]);
    }
    return parts.length === 0 ? 'Set packaging' : `Set packaging to ${parts.join(', ')}`;
  },
});

/** Replace the recipe's notes. */
export const recipeSetNotes = defineCommand({
  type: 'recipe.setNotes',
  payload: p.recipeSetNotesPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      notes: command.payload.notes,
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.setNotes',
          { recipeId: prior.id, notes: prior.notes },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) =>
    command.payload.notes.length === 0 ? 'Clear the recipe notes' : 'Update the recipe notes',
});

/** Replace the recipe's tags. */
export const recipeSetTags = defineCommand({
  type: 'recipe.setTags',
  payload: p.recipeSetTagsPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...recipe,
      tags: [...command.payload.tags],
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(
          command,
          'recipe.setTags',
          { recipeId: prior.id, tags: [...prior.tags] },
          prior.updatedAt,
        );
  },
  summarize: (_state, command) =>
    command.payload.tags.length === 0
      ? 'Clear the recipe tags'
      : `Set tags to ${command.payload.tags.join(', ')}`,
});

/**
 * Scale the recipe: by a size factor, by a change of brewhouse efficiency, or
 * by both at once. See `document/scale.ts` for what moves under each.
 *
 * **Both axes in one command** because one press of *Scale* is one edit. The
 * dialog offers a new batch size and a "written for X %, my rig does Y %" pair
 * side by side, and a brewer who changes both and presses once expects a single
 * ⌘Z to put the recipe back. A second command would need a transaction concept
 * this system does not have, so the two multiplications compose here instead —
 * size first, then efficiency.
 *
 * Its inverse is a full `recipe.restore` rather than a scale by `1/k`: binary
 * floating point makes `x × 3 × (1/3) !== x` for most `x`, and an undo that
 * leaves 4.4999999999999996 kg where 4.5 kg used to be is not an undo. That
 * choice is what makes the composed case free — a restore is exact however many
 * factors were multiplied together on the way out.
 */
export const recipeScale = defineCommand({
  type: 'recipe.scale',
  payload: p.recipeScalePayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => {
      const sized = scaleRecipeDoc(recipe, command.payload.factor);
      const { efficiency } = command.payload;
      return efficiency === undefined ? sized : rescaleEfficiencyDoc(sized, efficiency);
    }),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(command, 'recipe.restore', { recipe: prior }, prior.updatedAt);
  },
  summarize: (state, command) => {
    const { factor, efficiency } = command.payload;
    const prior = state.recipes[command.payload.recipeId];
    const to = prior === undefined ? '' : ` (${volume(prior.batchVolumeL * factor)})`;
    const size = `Scale the recipe by ×${num(factor, 4)}${to}`;
    if (efficiency === undefined) return size;
    const move = `the grist ${transition(
      percent(efficiency.fromPercent),
      percent(efficiency.toPercent),
    )} brewhouse efficiency`;
    // A factor of 1 means the brewer moved only the efficiency; leading with
    // "×1" would bury the edit that actually happened.
    return factor === 1 ? `Rescale ${move}` : `${size}, and rescale ${move}`;
  },
});

/**
 * Roll a recipe back to a historical state (PLAN-LAUNCH D3).
 *
 * **The log is never rewritten.** Version history reads the append-only command
 * log and named snapshots; rolling back *appends* — this command carries the old
 * document forward, so the sync log stays a gap-free, dedupe-able sequence and
 * every device converges on the rollback the same way it converges on a rename.
 *
 * Three fields survive the rollback because they belong to the recipe's
 * identity rather than to its content: `id`, `createdAt`, and `updatedAt` (which
 * becomes the envelope's time — the recipe *was* modified, just now). Everything
 * else comes from the historical document. That is the difference from
 * `recipe.restore`, which is a verbatim upsert and re-installs the old
 * timestamps too.
 *
 * Its inverse is a full `recipe.restore` for the same reason `recipe.scale`'s
 * is: the pre-state document is the only exact description of what was undone.
 */
export const recipeRevertTo = defineCommand({
  type: 'recipe.revertTo',
  payload: p.recipeRevertToPayloadSchema,
  apply: (state, command) =>
    withRecipe(state, command, command.payload.recipeId, (recipe) => ({
      ...command.payload.recipe,
      schemaVersion: RECIPE_SCHEMA_VERSION,
      id: recipe.id,
      createdAt: recipe.createdAt,
    })),
  invert: (state, command) => {
    const prior = state.recipes[command.payload.recipeId];
    return prior === undefined
      ? null
      : inverseCommand(command, 'recipe.restore', { recipe: prior }, prior.updatedAt);
  },
  summarize: (state, command) =>
    `Revert recipe ${quoted(recipeName(state, command.payload.recipeId))} to ${
      command.payload.label
    }`,
});
